import React, { useState, useRef, useEffect } from "react";
import DropDownArrow from "../../Asserts/images/black_dropdown.svg"

const Dropdown = ({ data, name, setSelectedGame, setSelectedRounds, className = "" }) => {
  const [selectedValue, setSelectedValue] = useState("");
  const [open, setOpen] = useState(false);
  const [dropdownDirection, setDropdownDirection] = useState("down");
  const ref = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside or scrolling
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleScroll(e) {
      if (open && dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [open]);

  // Determine dropdown direction and positioning based on available space
  useEffect(() => {
    if (open && ref.current && dropdownRef.current) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = 224; 

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDropdownDirection("up");
      } else {
        setDropdownDirection("down");
      }

      // Adjust horizontal position if dropdown goes off screen
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      
      if (dropdownRect.right > viewportWidth) {
        dropdownRef.current.style.right = '0px';
        dropdownRef.current.style.left = 'auto';
      } else {
        dropdownRef.current.style.left = '0px';
        dropdownRef.current.style.right = 'auto';
      }
    }
  }, [open]);

  const handleOptionClick = (selectedId) => {
    if (selectedId === "All" || selectedId === "All Rounds") {
      setSelectedValue("");
      setSelectedGame((prev) => {
        const updated = { ...prev };
        if (name === "sport") {
          updated.sport = null;
          updated.league = null; 
        } else if (name === "league") {
          updated.league = null;
        } else if (name === "round") {
          updated.roundState = null;
        } else if (name === "completedRound") {
          setSelectedRounds(null);
        }
        return updated;
      });
      setOpen(false);
      return;
    }

    const selectedObj = data.find((ele) => ele.id.toString() === selectedId);
    const selectedName =
      selectedObj?.game_name || selectedObj?.name || selectedObj?.label || "";

    if (name === "sport") {
      setSelectedGame((prev) => ({
        ...prev,
        sport: {
          id: selectedId,
          name: selectedName,
        },
        league: null, 
      }));
    }

    if (name === "league") {
      setSelectedGame((prev) => ({
        ...prev,
        league: {
          id: selectedId,
          name: selectedName,
        },
      }));
    }

    if (name === "round") {
      setSelectedGame((prev) => ({
        ...prev,
        roundState: {
          id: selectedId,
          name: selectedName,
          value:
            selectedName === "Active Round"
              ? "active"
              : selectedName === "Inactive Round"
              ? "inactive"
              : "",
        },
      }));
    }

    if (name === "completedRound") {
      setSelectedRounds({
        id: selectedId,
        name: selectedName,
        value: selectedObj?.value || null,
      });
      setSelectedGame((prev) => ({
        ...prev,
        roundState: {
          id: selectedId,
          name: selectedName,
          value: selectedObj?.value || null,
        },
      }));
    }

    setSelectedValue(selectedId);
    setOpen(false);
  };

  // Get display text for selected value
  const getDisplayText = () => {
    if (!selectedValue) {
      return name === "sport" ? "Sport" : name === "league" ? "League" : name === "completedRound" ? "All Rounds" : "Round";
    }
    
    const selectedObj = data.find((ele) => ele.id.toString() === selectedValue);
    return selectedObj?.game_name || selectedObj?.name || selectedObj?.label || "";
  };

  // Create options array including "All" option
  const options = [
    ...(data && data.length > 0 ? data.map(ele => ({
      value: ele.id.toString(),
      label: ele?.game_name || ele.name || ele.label
    })) : []),
    ...(name !== "completedRound" ? [{ value: "All", label: "All" }] : [])
  ];

  return (
    <div ref={ref} className={`relative w-full flex-1 min-w-0 ${className}`}>
      <button
        type="button"
        className={`
          flex items-center justify-between px-2 sm:px-3 py-2 h-10 rounded-md bg-white border border-[#d9d9d9] 
          text-xs sm:text-sm font-medium text-[#222] w-full
          focus:outline-none transition shadow font-inter
          ${open ? "ring-2 ring-blue-200" : ""}
        `}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="truncate font-archivo font-semibold flex-1 text-left">
          {getDisplayText()}
        </span>
        <img src={DropDownArrow} alt="Dropdown Arrow" className={`ml-1 sm:ml-2 w-3 h-3 sm:w-4 sm:h-4 transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>
      
      {open && (
        <ul
          ref={dropdownRef}
          className={`
            absolute z-[9999] bg-white border border-[#E0E6F0] rounded-lg shadow-lg max-h-56 overflow-auto
            ${dropdownDirection === "up" ? "bottom-full mb-2" : "top-full mt-2"}
            w-full min-w-[160px] sm:min-w-[200px] md:min-w-[220px] max-w-[300px]
          `}
          style={{
            // Ensure dropdown doesn't go off screen - will be handled by useEffect
          }}
        >
          {data && data?.length > 0 ? (
            <>
              {options.map((opt) => (
                <li
                  key={opt.value}
                  onClick={() => handleOptionClick(opt.value)}
                  className={`px-2 font-archivo sm:px-3 py-2 cursor-pointer hover:bg-[#F5F7FA] text-[#000] text-xs sm:text-sm break-words ${
                    opt.value === selectedValue ? "bg-[#F5F7FA]" : ""
                  }`}
                  style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                >
                  {opt.label}
                </li>
              ))}
            </>
          ) : (
            <li className="px-2 sm:px-3 font-archivo py-2 text-gray-400 cursor-not-allowed text-xs sm:text-sm">
              No options available
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;